import React, { useEffect, useRef, useState } from "react";
import api from "../api/client";
import { toast } from "sonner";
import { CreditCard, Upload, CheckCircle2, Eye, Trash2, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Panneau dédié à la pièce d'identité (CNI / Passeport) du client.
 *  - 2 slots : Recto / Verso
 *  - Image preview (si JPG/PNG) ou icône PDF
 *  - Upload via /api/documents/upload avec category=cni_recto / cni_verso
 *  - L'API met automatiquement à jour client.cni_recto_doc_id et client.cni_verso_doc_id
 */
export default function CNIPanel({ clientId, client, onUpdate }) {
  const [docs, setDocs] = useState({ recto: null, verso: null });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({ recto: false, verso: false });
  const rectoRef = useRef(null);
  const versoRef = useRef(null);
  const [preview, setPreview] = useState(null);

  const load = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/documents", { params: { owner_type: "client", owner_id: clientId } });
      const recto = data.find((d) => d.category === "cni_recto");
      const verso = data.find((d) => d.category === "cni_verso");
      setDocs({ recto, verso });
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [clientId]); // eslint-disable-line

  const upload = async (side, file) => {
    if (!file) return;
    setUploading({ ...uploading, [side]: true });
    try {
      const fd = new FormData();
      fd.append("owner_type", "client");
      fd.append("owner_id", clientId);
      fd.append("category", `cni_${side}`);
      fd.append("label", `CNI ${side === "recto" ? "Recto" : "Verso"}`);
      fd.append("file", file);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`CNI ${side === "recto" ? "recto" : "verso"} ajoutée`);
      await load();
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setUploading({ ...uploading, [side]: false });
    }
  };

  const remove = async (doc) => {
    if (!doc || !window.confirm("Supprimer cette pièce ?")) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Supprimé");
      await load();
      onUpdate?.();
    } catch {
      toast.error("Erreur");
    }
  };

  if (loading) {
    return <div className="tpco-card p-6"><Loader2 className="w-5 h-5 animate-spin text-[#1E3A8A]" /></div>;
  }

  const both = docs.recto && docs.verso;

  return (
    <>
      <div className="tpco-card p-6" data-testid="cni-panel">
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-start gap-3">
            <div className={`w-11 h-11 rounded-md flex items-center justify-center shrink-0 ${both ? "bg-[#E5F0E8] text-[#2D7A3F]" : "bg-[#FFF4E5] text-[#7A5A1A]"}`}>
              {both ? <CheckCircle2 className="w-6 h-6" /> : <CreditCard className="w-6 h-6" />}
            </div>
            <div>
              <h3 className="font-display text-base font-bold text-[#0F1A3D]">Pièce d'identité (CNI / Passeport)</h3>
              <div className="text-xs text-[#4B5775] mt-1">
                {both ? <>Recto + verso fournis · KYC complet ✓</> : <>Importez les <strong>2 faces</strong> de la pièce d'identité pour valider le KYC client.</>}
              </div>
            </div>
          </div>
          <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${both ? "bg-[#E5F0E8] text-[#2D7A3F]" : "bg-[#FFF4E5] text-[#7A5A1A]"}`}>
            {both ? "Complet" : `${(docs.recto ? 1 : 0) + (docs.verso ? 1 : 0)}/2`}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Slot
            side="recto"
            label="Recto"
            doc={docs.recto}
            uploading={uploading.recto}
            onPick={() => rectoRef.current?.click()}
            onPreview={() => docs.recto && setPreview(docs.recto)}
            onRemove={() => remove(docs.recto)}
          />
          <Slot
            side="verso"
            label="Verso"
            doc={docs.verso}
            uploading={uploading.verso}
            onPick={() => versoRef.current?.click()}
            onPreview={() => docs.verso && setPreview(docs.verso)}
            onRemove={() => remove(docs.verso)}
          />
        </div>

        <input ref={rectoRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" hidden onChange={(e) => upload("recto", e.target.files?.[0])} data-testid="cni-recto-file" />
        <input ref={versoRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" hidden onChange={(e) => upload("verso", e.target.files?.[0])} data-testid="cni-verso-file" />
      </div>

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="max-w-3xl w-full bg-white rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 flex items-center justify-between border-b border-[#E2E5F1]">
              <div className="font-bold text-[#0F1A3D]">{preview.label}</div>
              <button onClick={() => setPreview(null)} className="text-sm text-[#4B5775] hover:text-[#0F1A3D]">Fermer</button>
            </div>
            <div className="p-4 max-h-[80vh] overflow-auto">
              {preview.content_type?.startsWith("image/") ? (
                <img src={`${BACKEND}/api/documents/${preview.id}/download`} alt={preview.label} className="max-w-full mx-auto" />
              ) : (
                <iframe src={`${BACKEND}/api/documents/${preview.id}/download`} title={preview.label} className="w-full h-[70vh]" />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Slot({ side, label, doc, uploading, onPick, onPreview, onRemove }) {
  const isImg = doc?.content_type?.startsWith("image/");
  return (
    <div className={`border-2 ${doc ? "border-solid border-[#E2E5F1]" : "border-dashed border-[#E2E5F1] hover:border-[#1E3A8A]"} rounded-lg overflow-hidden bg-[#F8FAFC]`} data-testid={`cni-slot-${side}`}>
      <div className="px-3 py-2 bg-white border-b border-[#E2E5F1] flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-wider text-[#0F1A3D]">{label}</div>
        {doc && <span className="text-[10px] text-[#2D7A3F] font-bold inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> OK</span>}
      </div>
      {!doc ? (
        <button type="button" onClick={onPick} disabled={uploading} className="w-full h-40 flex flex-col items-center justify-center text-[#4B5775] hover:text-[#1E3A8A] hover:bg-[#E8EBFA]/40 transition disabled:opacity-50" data-testid={`cni-upload-${side}`}>
          {uploading ? <Loader2 className="w-6 h-6 animate-spin mb-2" /> : <Upload className="w-6 h-6 mb-2" />}
          <div className="text-xs font-bold">{uploading ? "Envoi…" : "Cliquez pour téléverser"}</div>
          <div className="text-[10px] mt-1">JPG · PNG · PDF (max 10 Mo)</div>
        </button>
      ) : (
        <div className="relative group">
          {isImg ? (
            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/download`} alt={label} className="w-full h-40 object-cover bg-white" />
          ) : (
            <div className="w-full h-40 flex items-center justify-center bg-white">
              <div className="text-center">
                <CreditCard className="w-10 h-10 text-[#E91E63] mx-auto" strokeWidth={1.5} />
                <div className="text-xs text-[#4B5775] mt-2 font-bold">PDF</div>
              </div>
            </div>
          )}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
            <button onClick={onPreview} className="w-9 h-9 bg-white rounded-md inline-flex items-center justify-center hover:bg-[#1E3A8A] hover:text-white" title="Voir"><Eye className="w-4 h-4" /></button>
            <button onClick={onRemove} className="w-9 h-9 bg-white rounded-md inline-flex items-center justify-center hover:bg-[#C83B3B] hover:text-white" title="Supprimer"><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}
